import React, { useState } from 'react';
import { Mail, Copy, Check, Inbox, FileText, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusBadge = (status) => {
  const config = {
    pending: { label: 'Processing', variant: 'secondary', icon: Clock },
    processing: { label: 'Processing', variant: 'secondary', icon: Clock },
    completed: { label: 'Completed', variant: 'success', icon: CheckCircle2 },
    failed: { label: 'Failed', variant: 'destructive', icon: AlertCircle },
  };
  return config[status] || config.pending;
};

const InboundEmailSection = ({ caseData, inboundEmails = [] }) => {
  const [copied, setCopied] = useState(false);
  const inboundEmailAddress = caseData?.inbound_email_address;

  const handleCopy = async () => {
    if (!inboundEmailAddress) return;

    try {
      await navigator.clipboard.writeText(inboundEmailAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!inboundEmailAddress) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-slate-600" />
        <h3 className="font-semibold text-slate-900">Forward Documents</h3>
      </div>

      {/* Email Address Card */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
        <p className="text-xs text-slate-600 mb-2">
          Forward booking confirmations, airline emails, or receipts to:
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white/80 px-3 py-2 rounded-md text-xs font-mono text-blue-700 border border-blue-200 break-all">
            {inboundEmailAddress}
          </code>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  className="shrink-0 h-9 w-9 border-blue-200 hover:bg-blue-100"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4 text-blue-600" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{copied ? 'Copied!' : 'Copy email address'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Documents will be automatically categorized and processed.
        </p>
      </div>

      {/* Received Emails List */}
      {inboundEmails.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Inbox className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">
              Received Emails ({inboundEmails.length})
            </span>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {inboundEmails.map((email) => {
              const statusConfig = getStatusBadge(email.status);
              const StatusIcon = statusConfig.icon;

              return (
                <div
                  key={email.id}
                  className="bg-slate-50 rounded-lg p-3 border border-slate-100"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">
                        {email.subject || '(No Subject)'}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        From: {email.from_email}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatDate(email.received_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {email.attachment_count > 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 text-slate-500">
                                <FileText className="h-3.5 w-3.5" />
                                <span className="text-xs">{email.attachment_count}</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{email.attachment_count} attachment(s)</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <Badge variant={statusConfig.variant} className="text-xs">
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </div>

                  {email.status === 'failed' && email.error_message && (
                    <p className="text-xs text-red-600 mt-2 bg-red-50 p-2 rounded">
                      {email.error_message}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {inboundEmails.length === 0 && (
        <div className="text-center py-4 text-slate-500">
          <Inbox className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">No emails received yet</p>
          <p className="text-xs text-slate-400">
            Forward documents to the email address above
          </p>
        </div>
      )}
    </div>
  );
};

export default InboundEmailSection;
